import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth';

const Callback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  
  // Use a ref to track if the callback has been handled
  const isHandled = React.useRef(false);
  
  // Track retry attempts
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 1; // Allow one automatic retry
  
  // Define handleCallback with useCallback to prevent infinite loops
  const handleCallback = useCallback(async () => {
    // Skip if already handled to prevent duplicate requests
    if (isHandled.current) {
      console.log('Callback already handled, skipping duplicate request');
      return;
    }
    
    try {
      // Mark as handled immediately to prevent race conditions
      isHandled.current = true;
      
      // Get the authorization code and state from the URL
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      
      if (!code) {
        const error = params.get('error');
        throw new Error(error || 'No authorization code received');
      }
      
      console.log('Handling Spotify callback in Callback component');
      console.log('State parameter present:', !!state);
      
      // Exchange the code for tokens, passing the state parameter
      const success = await authService.handleCallback(code, state || undefined);
      
      if (!success) {
        // If first attempt failed and we haven't retried yet, try again
        if (retryCount < maxRetries) {
          console.log(`Authentication failed, retrying (attempt ${retryCount + 1})`);
          setRetryCount(prev => prev + 1);
          // Reset the handled flag to allow another attempt
          isHandled.current = false;
          // Retry the login process
          authService.login();
          return;
        }
        
        throw new Error('Failed to authenticate with Spotify');
      }
      
      console.log('Authentication successful, redirecting to home page');
      
      // Redirect to home page
      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      
      // Redirect to login page after a delay
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    }
  }, [location, navigate, retryCount, maxRetries, setError]);
  
  useEffect(() => {
    handleCallback();
    
    // Cleanup function
    return () => {
      console.log('Callback component unmounting');
    };
  }, [handleCallback]);
  
  // Handle manual retry
  const handleManualRetry = () => {
    console.log('Manual retry requested');
    // Reset the handled flag to allow another attempt
    isHandled.current = false;
    // Reset error state
    setError(null);
    // Retry the callback handling
    handleCallback();
  };
  
  // Handle return to login
  const handleReturnToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      {error ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleManualRetry}
            >
              Try Again
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleReturnToLogin}
            >
              Return to Login
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
            If you continue to experience issues, please try clearing your browser cookies and cache.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Connecting to Spotify
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we complete the authentication process...
          </Typography>
          {retryCount > 0 && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Retrying authentication... (Attempt {retryCount + 1})
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Callback;
